import { useState, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";
import { Card, PageHeader, GoldBtn, GhostBtn, SectionTitle, Field, Input, Textarea, Select, Toggle } from "../components/common/Ui";

const TABS = [
  { id:"general",   label:"General",        icon:"🏪" },
  { id:"payments",  label:"Payments",       icon:"💳" },
  { id:"shipping",  label:"Shipping",       icon:"🚚" },
  { id:"email",     label:"Email / SMTP",   icon:"📧" },
  { id:"social",    label:"Social Media",   icon:"📱" },
  { id:"seo",       label:"SEO",            icon:"🔍" },
  { id:"security",  label:"Security",       icon:"🔐" },
];

const DEFAULTS = {
  general: { storeName:"", tagline:"", email:"", phone:"", address:"", currency:"INR", timezone:"Asia/Kolkata", gst:"", maintenance:false },
  payments: { razorpay:false, cashfree:false, stripe:false, paypal:false, cod:false, upi:false, razorpayKey:"", cashfreeKey:"" },
  shipping: { freeAbove:"500", standardRate:"60", expressRate:"120", shiprocket:false, delhivery:false, blueDart:false },
  email: { smtpHost:"", smtpPort:"587", smtpUser:"", smtpPass:"", fromName:"", orderConfirm:true, shipUpdate:true },
  social: { instagram:"", facebook:"", pinterest:"", youtube:"", whatsapp:"", ga4:"" },
  seo: { siteTitle:"", siteDesc:"", keywords:"", ogImage:"", robots:"index,follow" },
};

export default function SettingsAdmin() {
  const [tab, setTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [general, setGeneral]   = useState(DEFAULTS.general);
  const [payments, setPayments] = useState(DEFAULTS.payments);
  const [shipping, setShipping] = useState(DEFAULTS.shipping);
  const [email, setEmail]       = useState(DEFAULTS.email);
  const [social, setSocial]     = useState(DEFAULTS.social);
  const [seo, setSeo]           = useState(DEFAULTS.seo);

  const showToast = (msg, color = T.gold) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2500);
  };

  // ── Fetch settings from backend ─────────────────────────────────────────────
  const fetchSettings = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await apiFetch("/settings/get.php");
      const s = res.data;
      setGeneral({ ...DEFAULTS.general, ...s.general });
      setPayments({ ...DEFAULTS.payments, ...s.payments });
      setShipping({ ...DEFAULTS.shipping, ...s.shipping });
      setEmail({ ...DEFAULTS.email, ...s.email });
      setSocial({ ...DEFAULTS.social, ...s.social });
      setSeo({ ...DEFAULTS.seo, ...s.seo });
    } catch (err) {
      setLoadError(err.message || "Failed to load settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const sg = (k, v) => setGeneral(p => ({ ...p, [k]: v }));
  const sp = (k, v) => setPayments(p => ({ ...p, [k]: v }));
  const ss = (k, v) => setShipping(p => ({ ...p, [k]: v }));
  const se = (k, v) => setEmail(p => ({ ...p, [k]: v }));
  const sc = (k, v) => setSocial(p => ({ ...p, [k]: v }));
  const seo_ = (k, v) => setSeo(p => ({ ...p, [k]: v }));

  const sectionDataMap = { general, payments, shipping, email, social, seo };

  // ── Save current tab's section to backend ───────────────────────────────────
  const handleSave = async () => {
    if (tab === "security") {
      showToast("Security settings are not saved from here yet.", T.warning);
      return;
    }
    setSaving(true);
    try {
      await apiFetch("/settings/update.php", {
        method: "PUT",
        body: { section: tab, data: sectionDataMap[tab] },
      });
      showToast(`✓ ${TABS.find(t=>t.id===tab)?.label} settings saved`, T.success);
    } catch (err) {
      showToast(err.message || "Failed to save settings.", T.danger);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => fetchSettings();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-2">
          <div className="w-8 h-8 rounded-full animate-spin"
            style={{ border: `3px solid ${T.border}`, borderTopColor: T.gold }} />
          <p className="text-[12px]" style={{ color: T.textSec }}>Loading settings...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-2xl bg-white p-6 text-center" style={{ border: `1px solid ${T.border}` }}>
        <p className="text-[13px] font-semibold mb-2" style={{ color: T.danger }}>{loadError}</p>
        <button onClick={fetchSettings} className="px-4 py-2 rounded-xl text-[12px] font-semibold text-white" style={{ background: T.gold }}>Retry</button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure your store settings">
        <GhostBtn onClick={handleReset}>↺ Reset</GhostBtn>
        <GoldBtn onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "💾 Save Changes"}</GoldBtn>
      </PageHeader>

      <div className="flex flex-col md:flex-row gap-3 md:gap-5">
        <div className="w-full md:w-48 flex-shrink-0 flex md:flex-col gap-1.5 md:gap-0.5 overflow-x-auto md:overflow-visible pb-1 md:pb-0">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className="w-auto md:w-full flex-shrink-0 flex items-center gap-2 md:gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all text-[12px] md:text-[13px] whitespace-nowrap"
              style={tab === t.id
                ? { background:T.goldL, color:T.goldD, fontWeight:600 }
                : { color:T.textSec }}>
              <span>{t.icon}</span>{t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 space-y-4 md:space-y-5 min-w-0">

          {tab === "general" && (
            <>
              <Card className="p-4 sm:p-5">
                <SectionTitle>Store Information</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Store Name"><Input value={general.storeName} onChange={e=>sg("storeName",e.target.value)} /></Field>
                  <Field label="Tagline"><Input value={general.tagline} onChange={e=>sg("tagline",e.target.value)} /></Field>
                  <Field label="Support Email"><Input value={general.email} onChange={e=>sg("email",e.target.value)} type="email" /></Field>
                  <Field label="Phone"><Input value={general.phone} onChange={e=>sg("phone",e.target.value)} /></Field>
                  <div className="sm:col-span-2"><Field label="Address"><Input value={general.address} onChange={e=>sg("address",e.target.value)} /></Field></div>
                  <Field label="Currency">
                    <Select value={general.currency} onChange={e=>sg("currency",e.target.value)}>
                      <option value="INR">INR — Indian Rupee (₹)</option>
                      <option value="USD">USD — US Dollar ($)</option>
                    </Select>
                  </Field>
                  <Field label="Timezone">
                    <Select value={general.timezone} onChange={e=>sg("timezone",e.target.value)}>
                      <option>Asia/Kolkata</option><option>Asia/Dubai</option><option>Europe/London</option>
                    </Select>
                  </Field>
                  <Field label="GST Number"><Input value={general.gst} onChange={e=>sg("gst",e.target.value)} placeholder="27ABCDE1234F1ZR" /></Field>
                </div>
              </Card>
              <Card className="p-4 sm:p-5">
                <SectionTitle>Store Status</SectionTitle>
                <Toggle checked={general.maintenance} onChange={v=>sg("maintenance",v)} label="Enable Maintenance Mode (hides store from visitors)" />
              </Card>
            </>
          )}

          {tab === "payments" && (
            <Card className="p-4 sm:p-5">
              <SectionTitle>Payment Gateways</SectionTitle>
              <div className="space-y-3 sm:space-y-4">
                {[
                  { key:"razorpay",  label:"Razorpay",  desc:"UPI, Cards, Netbanking" },
                  { key:"cashfree",  label:"Cashfree",  desc:"UPI, Cards, Netbanking" },
                  { key:"stripe",    label:"Stripe",    desc:"International Cards" },
                  { key:"paypal",    label:"PayPal",    desc:"International Payments" },
                  { key:"cod",       label:"Cash on Delivery", desc:"Pay on delivery" },
                  { key:"upi",       label:"UPI Direct",       desc:"GPay, PhonePe, BHIM" },
                ].map(g => (
                  <div key={g.key} className="flex items-center justify-between gap-3 p-3 rounded-xl" style={{ background:T.bgSoft }}>
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold" style={{ color:T.textPri }}>{g.label}</p>
                      <p className="text-[11px]" style={{ color:T.textSec }}>{g.desc}</p>
                    </div>
                    <Toggle checked={payments[g.key]} onChange={v => sp(g.key, v)} label="" />
                  </div>
                ))}
                {payments.razorpay && (
                  <Field label="Razorpay API Key">
                    <Input value={payments.razorpayKey} onChange={e=>sp("razorpayKey",e.target.value)} type="password" placeholder="rzp_live_..." />
                  </Field>
                )}
              </div>
            </Card>
          )}

          {tab === "shipping" && (
            <Card className="p-4 sm:p-5">
              <SectionTitle>Shipping Configuration</SectionTitle>
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="Free Shipping Above (₹)"><Input value={shipping.freeAbove} onChange={e=>ss("freeAbove",e.target.value)} placeholder="500" type="number" /></Field>
                  <Field label="Standard Shipping (₹)"><Input value={shipping.standardRate} onChange={e=>ss("standardRate",e.target.value)} placeholder="60" type="number" /></Field>
                  <Field label="Express Shipping (₹)"><Input value={shipping.expressRate} onChange={e=>ss("expressRate",e.target.value)} placeholder="120" type="number" /></Field>
                </div>
                <SectionTitle>Shipping Partners</SectionTitle>
                {[
                  { key:"shiprocket", label:"Shiprocket",  desc:"Pan India delivery" },
                  { key:"delhivery", label:"Delhivery",    desc:"Express delivery" },
                  { key:"blueDart",  label:"Blue Dart",    desc:"Premium delivery" },
                ].map(p => (
                  <div key={p.key} className="flex items-center justify-between gap-3 p-3 rounded-xl" style={{ background:T.bgSoft }}>
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold" style={{ color:T.textPri }}>{p.label}</p>
                      <p className="text-[11px]" style={{ color:T.textSec }}>{p.desc}</p>
                    </div>
                    <Toggle checked={shipping[p.key]} onChange={v => ss(p.key, v)} label="" />
                  </div>
                ))}
              </div>
            </Card>
          )}

          {tab === "email" && (
            <Card className="p-4 sm:p-5">
              <SectionTitle>SMTP Configuration</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="SMTP Host"><Input value={email.smtpHost} onChange={e=>se("smtpHost",e.target.value)} placeholder="smtp.gmail.com" /></Field>
                <Field label="SMTP Port"><Input value={email.smtpPort} onChange={e=>se("smtpPort",e.target.value)} placeholder="587" /></Field>
                <Field label="SMTP Username"><Input value={email.smtpUser} onChange={e=>se("smtpUser",e.target.value)} type="email" /></Field>
                <Field label="SMTP Password"><Input value={email.smtpPass} onChange={e=>se("smtpPass",e.target.value)} type="password" placeholder="••••••••" /></Field>
                <Field label="From Name"><Input value={email.fromName} onChange={e=>se("fromName",e.target.value)} placeholder="Aishcraft" /></Field>
              </div>
              <div className="space-y-3 mt-4">
                <Toggle checked={email.orderConfirm} onChange={v=>se("orderConfirm",v)} label="Send Order Confirmation Email" />
                <Toggle checked={email.shipUpdate}   onChange={v=>se("shipUpdate",v)}   label="Send Shipping Update Email" />
              </div>
            </Card>
          )}

          {tab === "social" && (
            <Card className="p-4 sm:p-5">
              <SectionTitle>Social Media Links</SectionTitle>
              <div className="space-y-4">
                {[
                  ["instagram","Instagram URL"],
                  ["facebook","Facebook URL"],
                  ["pinterest","Pinterest URL"],
                  ["youtube","YouTube URL"],
                  ["whatsapp","WhatsApp Number"],
                  ["ga4","Google Analytics 4 ID"],
                ].map(([k,l]) => (
                  <Field key={k} label={l}>
                    <Input value={social[k]} onChange={e=>sc(k,e.target.value)} placeholder={`Enter ${l}...`} />
                  </Field>
                ))}
              </div>
            </Card>
          )}

          {tab === "seo" && (
            <Card className="p-4 sm:p-5">
              <SectionTitle>Global SEO Settings</SectionTitle>
              <div className="space-y-4">
                <Field label="Site Title">
                  <Input value={seo.siteTitle} onChange={e=>seo_("siteTitle",e.target.value)} />
                  <p className="text-[10px] mt-1" style={{ color:seo.siteTitle.length>60?T.danger:T.textSec }}>{seo.siteTitle.length}/60</p>
                </Field>
                <Field label="Meta Description">
                  <Textarea value={seo.siteDesc} onChange={e=>seo_("siteDesc",e.target.value)} rows={2} />
                  <p className="text-[10px] mt-1" style={{ color:seo.siteDesc.length>160?T.danger:T.textSec }}>{seo.siteDesc.length}/160</p>
                </Field>
                <Field label="Meta Keywords"><Input value={seo.keywords} onChange={e=>seo_("keywords",e.target.value)} /></Field>
                <Field label="Robots">
                  <Select value={seo.robots} onChange={e=>seo_("robots",e.target.value)}>
                    <option value="index,follow">index, follow (recommended)</option>
                    <option value="noindex,nofollow">noindex, nofollow</option>
                  </Select>
                </Field>
              </div>
            </Card>
          )}

          {tab === "security" && (
            <Card className="p-4 sm:p-5">
              <SectionTitle>Security Settings</SectionTitle>
              <div className="space-y-4">
                <div className="p-4 rounded-2xl" style={{ background:T.bgSoft }}>
                  <p className="text-[13px] font-semibold mb-1" style={{ color:T.textPri }}>Two-Factor Authentication</p>
                  <p className="text-[11px] mb-3" style={{ color:T.textSec }}>Add an extra layer of security to your admin account.</p>
                  <GoldBtn small>Enable 2FA</GoldBtn>
                </div>
                <p className="text-[11px]" style={{ color: T.textSec }}>
                  Password change and API key management are not yet connected — coming soon.
                </p>
              </div>
            </Card>
          )}

        </div>
      </div>

      {toast && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-[100] px-5 py-3 rounded-2xl text-white text-[13px] font-semibold shadow-2xl text-center sm:text-left"
          style={{ background:toast.color }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}